import React, { useState } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  AlertCircle,
  RefreshCw,
  Play,
  Pause,
  ArrowUpRight,
  ArrowDownRight,
  Repeat,
  MoreHorizontal,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { PageLoader } from '@/components/ui/Spinner';
import { Modal } from '@/components/ui/Modal';
import { RecurringForm } from '@/components/recurring/RecurringForm';
import {
  useRecurring,
  useCreateRecurring,
  useUpdateRecurring,
  useDeleteRecurring,
  useProcessRecurring,
} from '@/hooks/useRecurring';
import { useTranslation } from '@/i18n';
import type { RecurringTransaction, CreateRecurringInput } from '@/types/recurring';
import { formatNextOccurrence } from '@/types/recurring';

export const RecurringPage: React.FC = () => {
  const { t, locale } = useTranslation();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRecurring, setEditingRecurring] = useState<RecurringTransaction | null>(null);
  const [deletingRecurring, setDeletingRecurring] = useState<RecurringTransaction | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const { data, isLoading, isError } = useRecurring();
  const createMutation = useCreateRecurring();
  const updateMutation = useUpdateRecurring();
  const deleteMutation = useDeleteRecurring();
  const processMutation = useProcessRecurring();

  const recurringList = data?.recurring || [];

  const formatCurrency = (value: number) => {
    const currencyMap: Record<string, string> = {
      'pt-BR': 'BRL',
      'en-GB': 'GBP',
      'es-ES': 'EUR',
      'fr-FR': 'EUR',
      'it-IT': 'EUR',
      'ar': 'SAR',
      'zh-CN': 'CNY',
    };
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyMap[locale] || 'GBP',
    }).format(value);
  };

  const getFrequencyLabel = (recurring: RecurringTransaction) => {
    switch (recurring.frequency) {
      case 'daily':
        return t.app.recurring.frequency.daily;
      case 'weekly':
        return t.app.recurring.frequency.weekly;
      case 'monthly':
        return `${t.app.recurring.form.day} ${recurring.dayOfMonth}`;
      case 'yearly':
        return t.app.recurring.frequency.yearly;
      default:
        return recurring.frequency;
    }
  };

  // Calculate totals (ensure amount is a valid number)
  const getMonthlyAmount = (r: RecurringTransaction): number => {
    const amount = Number(r.amount) || 0;
    if (r.frequency === 'monthly') return amount;
    if (r.frequency === 'weekly') return amount * 4;
    if (r.frequency === 'daily') return amount * 30;
    if (r.frequency === 'yearly') return amount / 12;
    return 0;
  };

  const totalMonthlyExpenses = recurringList
    .filter((r) => r.isActive && r.type === 'expense')
    .reduce((sum, r) => sum + getMonthlyAmount(r), 0);

  const totalMonthlyIncome = recurringList
    .filter((r) => r.isActive && r.type === 'income')
    .reduce((sum, r) => sum + getMonthlyAmount(r), 0);

  const handleCreate = () => {
    setEditingRecurring(null);
    setIsFormOpen(true);
  };

  const handleEdit = (recurring: RecurringTransaction) => {
    setEditingRecurring(recurring);
    setIsFormOpen(true);
    setOpenMenuId(null);
  };

  const handleFormSubmit = async (formData: CreateRecurringInput) => {
    if (editingRecurring) {
      await updateMutation.mutateAsync({
        id: editingRecurring.id,
        data: formData,
      });
    } else {
      await createMutation.mutateAsync(formData);
    }
    setIsFormOpen(false);
    setEditingRecurring(null);
  };

  const handleDelete = async () => {
    if (deletingRecurring) {
      await deleteMutation.mutateAsync(deletingRecurring.id);
      setDeletingRecurring(null);
    }
  };

  const handleToggle = async (recurring: RecurringTransaction) => {
    await updateMutation.mutateAsync({
      id: recurring.id,
      data: { isActive: !recurring.isActive },
    });
    setOpenMenuId(null);
  };

  const handleProcess = async (id: string) => {
    setProcessingId(id);
    setOpenMenuId(null);
    try {
      await processMutation.mutateAsync(id);
    } finally {
      setProcessingId(null);
    }
  };

  if (isLoading) {
    return <PageLoader message={t.app.recurring.loading} />;
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t.app.recurring.error}
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          {t.app.recurring.errorDesc}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t.app.recurring.title}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t.app.recurring.subtitle}
          </p>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={handleCreate}>
          {t.app.recurring.newRecurring}
        </Button>
      </div>

      {/* Summary Cards */}
      {recurringList.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="py-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">{t.app.recurring.summary?.monthlyExpenses || 'Monthly Expenses'}</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                -{formatCurrency(totalMonthlyExpenses)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">{t.app.recurring.summary?.monthlyIncome || 'Monthly Income'}</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                +{formatCurrency(totalMonthlyIncome)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">{t.app.recurring.summary?.netMonthly || 'Net Monthly'}</p>
              <p className={`text-2xl font-bold ${totalMonthlyIncome - totalMonthlyExpenses >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {formatCurrency(totalMonthlyIncome - totalMonthlyExpenses)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Empty State */}
      {recurringList.length === 0 ? (
        <Card className="p-12 text-center">
          <Repeat className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t.app.recurring.noRecurring}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto mb-6">
            {t.app.recurring.noRecurringDesc}
          </p>
          <Button onClick={handleCreate} leftIcon={<Plus className="w-4 h-4" />}>
            {t.app.recurring.createFirst}
          </Button>
        </Card>
      ) : (
        <Card padding="none">
          {/* Mobile list view */}
          <div className="md:hidden divide-y divide-gray-200 dark:divide-secondary-700">
            {recurringList.map((recurring) => (
              <div
                key={recurring.id}
                className={`p-4 ${!recurring.isActive ? 'opacity-60' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        recurring.type === 'income'
                          ? 'bg-green-100 dark:bg-green-900/30'
                          : 'bg-red-100 dark:bg-red-900/30'
                      }`}
                    >
                      {recurring.type === 'income' ? (
                        <ArrowUpRight className="w-4 h-4 text-green-600 dark:text-green-400" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4 text-red-600 dark:text-red-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {recurring.description}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {getFrequencyLabel(recurring)} · {formatNextOccurrence(recurring.nextOccurrence)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <p
                      className={`font-semibold ${
                        recurring.type === 'income'
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {recurring.type === 'income' ? '+' : '-'}
                      {formatCurrency(recurring.amount)}
                    </p>
                    <div className="relative">
                      <button
                        onClick={() => setOpenMenuId(openMenuId === recurring.id ? null : recurring.id)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700"
                      >
                        <MoreHorizontal className="w-4 h-4 text-gray-500" />
                      </button>
                      {openMenuId === recurring.id && (
                        <div className="absolute right-0 top-8 z-10 bg-white dark:bg-secondary-800 border border-gray-200 dark:border-secondary-700 rounded-lg shadow-lg py-1 min-w-[140px]">
                          <button
                            onClick={() => handleToggle(recurring)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-secondary-700 flex items-center gap-2"
                          >
                            {recurring.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                            {recurring.isActive ? t.app.recurring.actions.pause : t.app.recurring.actions.activate}
                          </button>
                          <button
                            onClick={() => handleEdit(recurring)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-secondary-700 flex items-center gap-2"
                          >
                            <Pencil className="w-4 h-4" />
                            {t.app.common.edit}
                          </button>
                          <button
                            onClick={() => {
                              setDeletingRecurring(recurring);
                              setOpenMenuId(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            {t.app.common.delete}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-secondary-700/50">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t.app.transactions.table.description}
                  </th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t.app.transactions.table.amount}
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t.app.recurring.form.frequencyLabel || 'Frequency'}
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t.app.recurring.next || 'Next'}
                  </th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t.app.common.status || 'Status'}
                  </th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t.app.transactions.table.actions}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-secondary-700">
                {recurringList.map((recurring) => (
                  <tr
                    key={recurring.id}
                    className={`hover:bg-gray-50 dark:hover:bg-secondary-700/50 ${!recurring.isActive ? 'opacity-60' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            recurring.type === 'income'
                              ? 'bg-green-100 dark:bg-green-900/30'
                              : 'bg-red-100 dark:bg-red-900/30'
                          }`}
                        >
                          {recurring.type === 'income' ? (
                            <ArrowUpRight className="w-4 h-4 text-green-600 dark:text-green-400" />
                          ) : (
                            <ArrowDownRight className="w-4 h-4 text-red-600 dark:text-red-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {recurring.description}
                          </p>
                          {recurring.categoryName && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {recurring.categoryName}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className={`font-semibold ${
                          recurring.type === 'income'
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-600 dark:text-red-400'
                        }`}
                      >
                        {recurring.type === 'income' ? '+' : '-'}
                        {formatCurrency(recurring.amount)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {getFrequencyLabel(recurring)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {formatNextOccurrence(recurring.nextOccurrence)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant={recurring.isActive ? 'success' : 'secondary'} className="text-xs">
                        {recurring.isActive ? t.app.recurring.active : t.app.recurring.paused}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleToggle(recurring)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700"
                          title={recurring.isActive ? t.app.recurring.actions.pause : t.app.recurring.actions.activate}
                        >
                          {recurring.isActive ? (
                            <Pause className="w-4 h-4 text-gray-500" />
                          ) : (
                            <Play className="w-4 h-4 text-gray-500" />
                          )}
                        </button>
                        {recurring.isActive && (
                          <button
                            onClick={() => handleProcess(recurring.id)}
                            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700"
                            title={t.app.recurring.actions.processNow}
                            disabled={processingId === recurring.id}
                          >
                            <RefreshCw className={`w-4 h-4 text-gray-500 ${processingId === recurring.id ? 'animate-spin' : ''}`} />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(recurring)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700"
                          title={t.app.common.edit}
                        >
                          <Pencil className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => setDeletingRecurring(recurring)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700"
                          title={t.app.common.delete}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <RecurringForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingRecurring(null);
        }}
        onSubmit={handleFormSubmit}
        recurring={editingRecurring}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingRecurring}
        onClose={() => setDeletingRecurring(null)}
        title={t.app.recurring.confirmDelete}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            {t.app.recurring.confirmDeleteMsg}{' '}
            <span className="font-semibold text-gray-900 dark:text-white">
              {deletingRecurring?.description}
            </span>
            ?
          </p>
          <p className="text-sm text-yellow-600 dark:text-yellow-500">
            {t.app.recurring.deleteWarning}
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={() => setDeletingRecurring(null)}>
              {t.app.common.cancel}
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              isLoading={deleteMutation.isPending}
            >
              {t.app.common.delete}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RecurringPage;
